<script lang="ts">
  /**
   * Botao de limpeza de sessao (T30).
   *
   * Exige confirmacao inline antes de executar a limpeza.
   * Apos confirmar, reseta o store e exibe toast de confirmacao.
   * Mantem privacyAccepted=true para evitar fricção desnecessaria.
   */
  import { clearDiagnosticSession } from '../../core/privacy/sessionCleanup';
  import { appStore } from '../../app/store';
  import type { AppStore } from '../../app/store';

  /** Store injetavel (facilita testes). Padrao: singleton do app. */
  export let store: AppStore = appStore;

  /**
   * Callback opcional para encerrar o Web Worker ativo.
   * Deve ser fornecido pela pagina/feature que controla o worker.
   */
  export let terminateWorker: (() => void) | undefined = undefined;

  let confirmVisible = false;
  let toastVisible = false;
  let toastTimer: ReturnType<typeof setTimeout> | null = null;

  function handleClearClick(): void {
    confirmVisible = true;
  }

  function handleConfirm(): void {
    confirmVisible = false;
    clearDiagnosticSession(store, terminateWorker);
    showToast();
  }

  function handleCancel(): void {
    confirmVisible = false;
  }

  function showToast(): void {
    toastVisible = true;
    if (toastTimer !== null) {
      clearTimeout(toastTimer);
    }
    toastTimer = setTimeout(() => {
      toastVisible = false;
      toastTimer = null;
    }, 3000);
  }
</script>

<div class="clear-session">
  {#if !confirmVisible}
    <button
      class="clear-btn"
      type="button"
      on:click={handleClearClick}
      aria-label="Limpar sessão de diagnóstico"
    >
      Limpar sessão
    </button>
  {:else}
    <div class="confirm-panel" role="alertdialog" aria-labelledby="confirm-msg">
      <p id="confirm-msg" class="confirm-msg">
        Isso vai apagar arquivos carregados, premissas e diagnóstico. Continuar?
      </p>
      <div class="confirm-actions">
        <button
          class="confirm-btn confirm-btn--confirm"
          type="button"
          on:click={handleConfirm}
        >
          Sim, limpar
        </button>
        <button
          class="confirm-btn confirm-btn--cancel"
          type="button"
          on:click={handleCancel}
        >
          Cancelar
        </button>
      </div>
    </div>
  {/if}

  {#if toastVisible}
    <div class="toast" role="status" aria-live="polite">
      Sessão limpa
    </div>
  {/if}
</div>

<style>
  .clear-session {
    position: relative;
    display: inline-block;
  }

  /* Botao principal — estilo neutro/secundario */
  .clear-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--ptt-space-2, 8px);
    padding: var(--ptt-space-2, 8px) var(--ptt-space-5, 24px);
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.5;
    color: var(--ptt-gray-200, #565656);
    background: transparent;
    border: 1px solid var(--ptt-gray-50, #c0c0c0);
    border-radius: var(--ptt-radius-sm, 4px);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    white-space: nowrap;
  }

  .clear-btn:hover {
    background: var(--ptt-gray-10, #f5f5f5);
    border-color: var(--ptt-gray-100, #7d7d7d);
    color: var(--ptt-gray-300, #323232);
  }

  .clear-btn:focus-visible {
    outline: none;
    box-shadow: var(--ptt-focus-ring, 0 0 0 3px rgba(0, 0, 0, 0.2));
  }

  /* Painel de confirmacao inline */
  .confirm-panel {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3, 12px);
    padding: var(--ptt-space-4, 16px);
    background: var(--ptt-white, #fff);
    border: 1px solid var(--ptt-gray-20, #e0e0e0);
    border-radius: var(--ptt-radius-sm, 4px);
    box-shadow: var(--ptt-shadow-sm, 0 2px 4px rgba(0, 0, 0, 0.08));
    min-width: 280px;
  }

  .confirm-msg {
    margin: 0;
    font-size: 0.875rem;
    color: var(--ptt-gray-300, #323232);
    line-height: 1.5;
  }

  .confirm-actions {
    display: flex;
    gap: var(--ptt-space-2, 8px);
  }

  .confirm-btn {
    flex: 1;
    padding: var(--ptt-space-2, 8px) var(--ptt-space-3, 12px);
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.5;
    border-radius: var(--ptt-radius-sm, 4px);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  .confirm-btn:focus-visible {
    outline: none;
    box-shadow: var(--ptt-focus-ring, 0 0 0 3px rgba(0, 0, 0, 0.2));
  }

  .confirm-btn--confirm {
    background: var(--ptt-gray-300, #323232);
    color: var(--ptt-white, #fff);
    border: 1px solid var(--ptt-gray-300, #323232);
  }

  .confirm-btn--confirm:hover {
    background: var(--ptt-black, #000);
    border-color: var(--ptt-black, #000);
  }

  .confirm-btn--cancel {
    background: transparent;
    color: var(--ptt-gray-200, #565656);
    border: 1px solid var(--ptt-gray-50, #c0c0c0);
  }

  .confirm-btn--cancel:hover {
    background: var(--ptt-gray-10, #f5f5f5);
    border-color: var(--ptt-gray-100, #7d7d7d);
  }

  /* Toast de confirmacao */
  .toast {
    position: absolute;
    top: calc(100% + var(--ptt-space-2, 8px));
    left: 0;
    padding: var(--ptt-space-2, 8px) var(--ptt-space-4, 16px);
    background: var(--ptt-gray-300, #323232);
    color: var(--ptt-white, #fff);
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: var(--ptt-radius-sm, 4px);
    white-space: nowrap;
    box-shadow: var(--ptt-shadow-sm, 0 2px 4px rgba(0, 0, 0, 0.08));
    pointer-events: none;
    z-index: 10;
  }
</style>
